import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Target, Eye, Zap, Users, Award, ArrowRight, 
  CheckCircle, Thermometer, Shield, MapPin
} from 'lucide-react';

const chairmanVision = {
  name: "",
  title: "Chairman Vision",
  message: `Track0tic was conceived with a simple but powerful belief: every medicine delivery should be verifiable, compliant, and trustworthy. By combining intelligent sensors, real-time monitoring, and advanced analytics, Track0tic brings a new level of accountability to pharmaceutical logistics — enabling stakeholders to ensure that medicines maintain their required conditions and reach their destination safely.

Our vision extends beyond tracking shipments. We aim to build a system that provides true delivery verification — confirming not only where a package traveled, but whether it was handled correctly, protected from tampering, and maintained within required environmental conditions.

When medicine integrity is protected, patient safety is protected.`
};

const values = [
  {
    icon: Shield,
    title: 'Patient Safety First',
    description: 'Every feature we build prioritizes the safety of patients receiving temperature-sensitive medications.'
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'Pushing boundaries with IoT technology and real-time monitoring solutions.'
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We work alongside pharmaceutical distributors as true technology partners.'
  },
  {
    icon: Award,
    title: 'Compliance Excellence',
    description: 'Committed to meeting and exceeding regulatory standards for pharmaceutical logistics.'
  }
];

const capabilities = [
  { icon: Thermometer, label: 'Temperature Monitoring', value: '2-8°C Range' },
  { icon: MapPin, label: 'GPS Tracking', value: 'Real-time' },
  { icon: Shield, label: 'Tamper Detection', value: 'Weight + Bag' },
  { icon: CheckCircle, label: 'Compliance', value: 'GDP Ready' }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="min-h-screen bg-white pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Badge className="mb-6 bg-blue-100 text-blue-700 border-blue-200">
                ABOUT TRACK0TIC
              </Badge>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Securing
                <span className="text-primary"> Pharmaceutical</span>
                <span className="block">Deliveries</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Track<span className="text-red-500">0</span>tic is an intelligent IoT platform 
                designed specifically for pharmaceutical cold chain logistics. We enable real-time 
                temperature monitoring, GPS tracking, and compliance documentation to ensure 
                medicines reach patients safely.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {capabilities.map((cap) => (
                  <div key={cap.label} className="text-center p-4 bg-white rounded-xl border border-gray-200">
                    <cap.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                    <div className="text-xs text-gray-500">{cap.label}</div>
                    <div className="font-semibold text-gray-900 text-sm">{cap.value}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1745939912168-cc7333d65327?w=600&h=500&fit=crop"
                  alt="Insulin pen and pharmaceutical cold chain"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">99.9%</div>
                      <div className="text-xs text-gray-500">Compliance Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Chairman's Vision */}
      <section className="py-20 bg-gray-50" data-testid="chairman-vision">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-4">
              <h2 className="text-xl font-semibold text-white">Chairman's Vision</h2>
            </div>
            <div className="p-8 md:p-12">
              <div className="text-gray-600 leading-relaxed space-y-4">
                {chairmanVision.message.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-lg">
                    {paragraph.includes('Track0tic') 
                      ? paragraph.split('Track0tic').map((part, i, arr) => (
                          <span key={i}>
                            {part}
                            {i < arr.length - 1 && <>Track<span className="text-red-500">0</span>tic</>}
                          </span>
                        ))
                      : paragraph
                    }
                  </p>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-xl">
                  AK
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-lg">{chairmanVision.name}</div>
                  <div className="text-gray-500">
                    {chairmanVision.title}, Track<span className="text-red-500">0</span>tic
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200"
            >
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To revolutionize pharmaceutical logistics with intelligent IoT monitoring that ensures 
                every temperature-sensitive medication maintains its efficacy from warehouse to patient. 
                We're committed to making cold chain compliance accessible, automated, and auditable.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200"
            >
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To become India's leading platform for pharmaceutical delivery verification, where 
                every shipment is traceable, every temperature log is immutable, and every delivery 
                is verifiable. We envision a future where medicine integrity is never compromised.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              OUR VALUES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
              What Drives Us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 text-center border border-gray-200 hover:border-primary hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Join Our Mission to
              <span className="block">Protect Patient Safety</span>
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Partner with Track<span className="text-red-300">0</span>tic to ensure your pharmaceutical 
              deliveries meet the highest standards of compliance and safety.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold h-14 px-8 rounded-lg"
                >
                  Contact Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/team">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 h-14 px-8 rounded-lg"
                >
                  Meet Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
